/**
 * Recovery after every move (duel v3 P1, the triangle with teeth). In a bout's fight (round 2 on, `DUEL_FLAGS.v2`; PvE and
 * the main game are untouched):
 * - a move that lands starts a recovery (data/moveTiming.ts: the move's own `recovery`, else by kind and weight); a move that
 *   found nothing (a dodge, no one in reach) starts a longer one, and a whiffed break the longest. In recovery the fighter's
 *   guard is down (a held guard is let go and comes back when the recovery ends if the key is still held), a dodge, a jump and
 *   a move are dropped like a lock form's commands, and the basic cut is held back. Walking is free. A stagger ends it.
 * - the breaks of the bout (`DUEL_TRI`): a cooldown of `breakCd` after one that connected, `breakCdWhiff` after a whiff, and a
 *   shorter wind-up (the damage on an open foe is in duelMatch's `mod`).
 * The rival's mind sees the recovery in its `FighterView` and punishes it by skill.
 */
import { DUEL_FLAGS, DUEL_TRI } from '../data/duelMatch';
import { recoveryOf } from '../data/moveTiming';
import { SKILLS } from '../data/skills';
import type { PlayerCommand, WorldEvent } from '../world';
import type { SystemHost, WorldSystem } from './types';

const SYSTEMS = new WeakMap<object, DuelRecoverySystem>();
export const duelRecoveryOf = (w: object): DuelRecoverySystem | null => SYSTEMS.get(w) ?? null;

/** Commands dropped in recovery (a guard raised is held back, see `refuse`). */
const DROPPED = new Set<PlayerCommand['t']>(['skill', 'dash', 'jump']);

interface Rec {
  t: number;
  len: number;
  whiff: boolean;
  skillId: string;
}

export class DuelRecoverySystem implements WorldSystem {
  readonly name = 'duelRecovery';
  private readonly rec = new Map<number, Rec>();
  /** Whether the guard key is held (dropped while recovering, given back after). */
  private readonly want = new Map<number, boolean>();
  private done: { src: number; skillId: string }[] = [];
  private landed = new Set<number>();

  constructor(private readonly h: SystemHost) {
    SYSTEMS.set(h.world, this);
  }

  /** Seconds of recovery left on a fighter (0: free). */
  left(id: number): number {
    const r = this.rec.get(id);
    return r ? Math.max(0, r.len - r.t) : 0;
  }

  /** The recovery under way (for the renderer to hold the end pose): length, seconds run, whether it is a whiff. */
  info(id: number): { len: number; t: number; whiff: boolean; skillId: string } | null {
    const r = this.rec.get(id);
    return r ? { ...r } : null;
  }

  /** A stop after a step, a landing or a missed dive (P1.5): the same lock as a move's recovery, tagged by `tag`. */
  startStop(id: number, len: number, tag: string): void {
    const e = this.h.world.get(id);
    if (!e || e.dead || e.stunT > 0 || len <= 0 || !this.inBout(id)) return;
    this.rec.set(id, { t: 0, len, whiff: false, skillId: tag });
    e.attackT = Math.max(e.attackT, len);
  }

  private inBout(id: number): boolean {
    const b = this.h.world.pvpBout;
    return DUEL_FLAGS.v2 && !!b && b.fight && (b.a === id || b.b === id);
  }

  refuse(cmd: PlayerCommand): boolean {
    const p = this.h.world.player;
    if (cmd.t === 'block') this.want.set(p.id, cmd.on);
    if (!this.rec.has(p.id)) return false;
    if (DROPPED.has(cmd.t)) return true;
    return cmd.t === 'block' && cmd.on;
  }

  observe(e: WorldEvent): void {
    if (e.type === 'castStart') {
      if (!this.inBout(e.srcId)) return;
      const s = SKILLS[e.skillId];
      const c = this.h.world.get(e.srcId)?.cast;
      if (s?.kind === 'break' && c) c.windup = Math.max(Math.min(c.windup, DUEL_TRI.windupFloor), c.windup - DUEL_TRI.windupCut);
    } else if (e.type === 'skill') {
      if (this.inBout(e.srcId)) this.done.push({ src: e.srcId, skillId: e.skillId });
    } else if (e.type === 'hit') {
      if (this.inBout(e.srcId) && e.outcome !== 'dodge') this.landed.add(e.srcId);
    }
  }

  step(dt: number): void {
    const w = this.h.world;
    const done = this.done;
    const landed = this.landed;
    this.done = [];
    this.landed = new Set();
    if (!w.pvpBout?.fight || !DUEL_FLAGS.v2) {
      this.rec.clear();
      this.want.clear();
      return;
    }
    for (const d of done) {
      const e = w.get(d.src);
      const s = SKILLS[d.skillId];
      if (!e || !s || e.dead || e.stunT > 0) continue;
      const whiff = !landed.has(d.src);
      if (s.kind === 'break') {
        e.cooldowns[s.id] = Math.max(e.cooldowns[s.id] ?? 0, whiff ? DUEL_TRI.breakCdWhiff : DUEL_TRI.breakCd);
      }
      const len = recoveryOf(s, whiff);
      if (len <= 0) continue;
      this.rec.set(d.src, { t: 0, len, whiff, skillId: s.id });
      e.attackT = Math.max(e.attackT, len);
    }
    for (const [id, r] of this.rec) {
      const e = w.get(id);
      const inp = w.players.get(id)?.input;
      if (!e || e.dead || e.stunT > 0 || !this.inBout(id)) {
        this.rec.delete(id);
        continue;
      }
      r.t += dt;
      if (r.t >= r.len) {
        this.rec.delete(id);
        if (inp && this.want.get(id)) inp.block = true;
        continue;
      }
      if (inp) inp.block = false;
      e.blocking = false;
    }
  }
}
